import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isEqual } from 'underscore';
import { TranslateService } from '@ngx-translate/core';

import { lastValueFrom } from 'rev-shared/rxjs/lastValueFrom';
import { AccessEntityType } from 'rev-shared/security/AccessEntityType';
import { DateParsersService } from 'rev-shared/date/DateParsers.Service';
import { FileWrapper } from 'rev-shared/ui/fileUpload/FileWrapper';
import { PushService } from 'rev-shared/push/PushService';
import { RecordingPolicy } from 'rev-shared/media/RecordingPolicy';
import { UserContextService } from 'rev-shared/security/UserContext.Service';
import { WebcastTemplatesService } from 'rev-shared/webcast/WebcastTemplates.Service';
import { formatIsoDate, formatTimespan } from 'rev-shared/date/DateFormatters';

import { ISearchResult } from 'rev-portal/search/Contract';
import { SearchConstants } from 'rev-portal/search/SearchConstants';
import { SearchService, IAccessEntityIdentifier } from 'rev-portal/search/Search.Service';

import { EditWebcastModel } from './EditWebcastModel';
import { EventAccessControl } from '../EventAccessControl';
import { IVideoSource } from './EditWebcast.Contract';
import { Poll } from '../polls/Poll';
import { PollsService } from '../polls/Polls.Service';
import { WebcastPresentationService } from '../presentations/WebcastPresentation.Service';
import { getReactionSettings, WebcastService } from '../webcast/Webcast.Service';
import { WebcastVideoSource } from '../webcast/WebcastVideoSource';
import { MediaFeaturesService } from 'rev-shared/media/MediaFeatures.Service';
import { SecurityContextService } from 'rev-shared/security/SecurityContext.Service';
import { UploadService } from 'rev-shared/media/Upload.Service';
import { VIEWER_ID_SETTING_HELPER } from 'rev-shared/viewerId/ViewerIdHelper';
import { ITemplateInfo } from 'rev-shared/webcast/WebcastTemplates.Contract';

import { WebcastType } from '../webcast/WebcastType';
import { ViewerIdPolicy } from 'rev-shared/viewerId/ViewerIdContract';

//move this to seprate file
export const AutomatedDatePastError = 'AutomatedWebcastDatePast';
export const InsufficientRevIQCredits = 'InsufficientRevIQCredits';
export const ShortcutNotAvailableError = 'WebcastShortcutNameAvailabilityNotValid';
export const TranscriptionIsNotEnabled = 'TranscriptionIsNotEnabled';
export const UnsupportedLiveSubtitlesSource = 'UnsupportedLiveSubtitlesSource';
export const UnsupportedLiveSubtitlesPresentationProfile = 'UnsupportedLiveSubtitlesPresentationProfile';
export const UploadFailedError = 'UploadFailed';
export const VideoSourceNotAvailableError = 'VideoSourceNotAvailable';
export const ViewingHoursNotAvailable = 'ViewingHoursNotAvailable';
export const RtmpListenerUrlConflict = 'RtmpListenerUrlConflict';
export const SecondaryRtmpListenerUrlConflict = 'SecondaryRtmpListenerUrlConflict';
export const InsufficientViewingHours = 'InsufficientViewingHours';
export const VodDoesNotExist = 'VodDoesNotExist';
export const WebcastVideoSourceNotAllowed = 'WebcastVideoSourceNotAllowed';
export const MicrosoftTeamsTenantIdRequired = 'MicrosoftTeamsTenantIdRequired';

export const LIVE_SUBTITLES_ERRORS = [
	InsufficientRevIQCredits,
	TranscriptionIsNotEnabled,
	UnsupportedLiveSubtitlesSource,
	UnsupportedLiveSubtitlesPresentationProfile,
	InsufficientViewingHours
];

const FORM_ERRORS: string[] = [
	AutomatedDatePastError,
	RtmpListenerUrlConflict,
	SecondaryRtmpListenerUrlConflict,
	ShortcutNotAvailableError,
	UploadFailedError,
	VideoSourceNotAvailableError,
	MicrosoftTeamsTenantIdRequired,
	...LIVE_SUBTITLES_ERRORS
];

@Injectable({
	providedIn: 'root'
})
export class EditWebcastService {

	constructor(
		private readonly http: HttpClient,
		private readonly DateParsers: DateParsersService,
		private readonly UploadService: UploadService,
		private readonly MediaFeatures: MediaFeaturesService,
		private readonly PollsService: PollsService,
		private readonly PushService: PushService,
		private readonly SearchService: SearchService,
		private readonly SecurityContext: SecurityContextService,
		private readonly UserContext: UserContextService,
		private readonly WebcastService: WebcastService,
		private readonly WebcastPresentationService: WebcastPresentationService,
		private readonly WebcastTemplatesService: WebcastTemplatesService,
		private readonly Translate: TranslateService
	) {
	}

	public getVideoSourceSchedule(startDate: Date, endDate: Date): Promise<IVideoSource[]> {
		return lastValueFrom<any>(this.http.get('/scheduled-events/accounts/video-sources/schedule', {
			params: {
				startDate: formatIsoDate(startDate),
				endDate: formatIsoDate(endDate)
			}
		}))
			.then(({ videoSources }) => {
				videoSources.forEach(source => {
					source.displayName = [source.deviceName, source.name].filter(Boolean).join(' - ');

					source.events.forEach(e => {
						Object.assign(e, {
							startDate: this.DateParsers.parseUTCDate(e.startDate),
							endDate: this.DateParsers.parseUTCDate(e.endDate),
							eventAdminFirstName: e.eventAdminFirstName || '',
							eventAdminLastName: e.eventAdminLastName || ''
						});
						const preProdDurationMs = this.DateParsers.parseTimespan(e.preProductionDuration);
						if (preProdDurationMs > 0) {
							e.startDate = new Date(e.startDate.getTime() - preProdDurationMs);
						}
					});

					source.presentationProfiles.forEach(profile => {
						profile.videoSource = source;
					});
				});

				return videoSources;
			});
	}

	public queryEventAdmins(query: string): Promise<ISearchResult> {
		const accountId = this.UserContext.getAccount().id;
		return this.SearchService
			.queryAccessEntities({
				accountId,
				query,
				type: [SearchConstants.accessEntityTypes.user],
				sortField: SearchConstants.nameSortField,
				sortAscending: true,
				count: SearchConstants.accessEntityPageSize,
				accountPermissions: [
					SearchConstants.rights.createEvents
				],
				noScroll: true
			})
			.then(result => ({
				items: result.accessEntities,
				count: result.totalHits
			}));
	}

	public queryMediaContributors(query: string): Promise<ISearchResult> {
		const accountId = this.UserContext.getAccount().id;
		return this.SearchService
			.queryAccessEntities({
				accountId,
				query,
				type: [SearchConstants.accessEntityTypes.user],
				sortField: SearchConstants.nameSortField,
				sortAscending: true,
				count: SearchConstants.accessEntityPageSize,
				accountPermissions: [
					SearchConstants.rights.createEvents,
					SearchConstants.rights.createMedia
				],
				noScroll: true
			})
			.then(result => ({
				items: result.accessEntities,
				count: result.totalHits
			}));
	}

	public deleteWebcast(webcastId: string): Promise<void> {
		return this.PushService.dispatchCommand('scheduledEvents:DeleteWebcast', { webcastId });
	}

	public getWebcast(webcastId: string): Promise<EditWebcastModel> {
		return this.WebcastService.getWebcast(webcastId, true, true)
			.then(webcast => Promise.all([
				webcast,
				webcast.pollsEnabled && this.PollsService.getPolls(webcastId),
				this.WebcastService.getWebcastEmbeddedContent(webcastId, webcast.currentUser.canEdit)
			]))
			.then(([webcast, polls, e]) => new EditWebcastModel(
				{
					...webcast,
					embeddedContent: e?.embeddedContent,
					polls
				},
				this.SecurityContext,
				this.SearchService,
				this.UserContext
			));
	}

	public initNewWebcastModel(data: any): Promise<EditWebcastModel> {
		const webcast = this.createNewWebcast(data);
		return this.getDefaultTemplate()
			.then(template => {
				return this.applyTemplateToWebcast(template, webcast);
			});
	}

	public cloneWebcast(webcastId: string, defaults: any): Promise<EditWebcastModel> {
		return this.getWebcast(webcastId).then(webcast => {

			const cloneWebcastDuration: number = +webcast.endDate - +webcast.startDate;
			const isUnableToSetPublic = !this.SecurityContext.checkAuthorization('events.setEventPublic') && webcast.accessControl === EventAccessControl.Public;
			const isUnableToSetTrustedPublic = !this.SecurityContext.checkAuthorization('events.setEventPublic') && webcast.accessControl === EventAccessControl.TrustedPublic;
			const isLiveSubtitles = this.SecurityContext.checkAuthorization('media.useRevIq') && webcast.liveSubtitles;
			const liveSubtitles = isLiveSubtitles ? webcast.liveSubtitles : { isLiveSubtitlesEnabled: false };
			webcast.externalPresenters.forEach(p => p.isNew = true);
			webcast.producerBgImages?.forEach(img => img.clone = true);
			Object.assign(webcast, {
				cloneWebcastId: webcast.id,
				endDate: new Date(+defaults.startDate + cloneWebcastDuration),
				id: undefined,
				startDate: defaults.startDate,
				title: this.Translate.instant('Event_CopyWebcastTitle', { '0': webcast.title }),
				originalPolls: {},
				polls: webcast.polls.map(poll => ({ ...poll, id: undefined })),
				accessControl: (isUnableToSetPublic || isUnableToSetTrustedPublic) ? EventAccessControl.AllUsers : webcast.accessControl,
				liveSubtitles,
				embeddedContent: webcast.embeddedContent && {
					isEnabled: webcast.embeddedContent.isEnabled,
					contentLinks: webcast.embeddedContent.contentLinks.map(link => ({ ...link, id: undefined, resourceUri: undefined, isEnabled: false }))
				},
				bannerDetails: webcast.bannerDetails && {
					isEnabled: webcast.bannerDetails.isEnabled,
					banners: webcast.bannerDetails.banners.map(link => ({ ...link, id: undefined, isEnabled: false }))
				},
				customConsentDetails: {
					isCustomConsentEnabled: webcast.isCustomConsentEnabled,
					consentVerbiage: webcast.consentVerbiage,
					customConsentVerbiageUpdates: []
				},
				estimatedAttendees: 0,
				isFeatured: webcast.isFeatured && this.SecurityContext.checkAuthorization('events.edit')
			});

			if(webcast.vodId) {
				return this.SearchService.userHasEditAccessToVideo(this.UserContext.getAccount().id, webcast.vodId)
					.then(hasEditAccess => {
						if (!hasEditAccess) {
							webcast.vodId = undefined;
							webcast.vodInfo = undefined;
						}
						return webcast;
					});
			}
			return webcast;
		});
	}

	public createNewWebcast(data: any): EditWebcastModel {
		return new EditWebcastModel(
			{
				eventAdminIds: [this.UserContext.getUser().id],
				presenterId: this.UserContext.getUser().id,
				presenter: { id: this.UserContext.getUser().id },
				recordingUploaderUserId: this.UserContext.getUser().id,
				...data,
				reactionsSettings: getReactionSettings(data.reactionsSettings)
			},
			this.SecurityContext,
			this.SearchService,
			this.UserContext
		);
	}

	public getDefaultTemplate(): Promise<ITemplateInfo> {
		return this.WebcastTemplatesService.getDefaultTemplate();
	}

	public applyTemplateToWebcast(template: ITemplateInfo, webcast: EditWebcastModel): Promise<EditWebcastModel> {
		if (!template) { return Promise.resolve(webcast); }

		if (template.metadata) {
			return webcast.applyTemplate(template).then(() => webcast);
		}

		webcast.customFields = template.customFields;
		return Promise.resolve(webcast);
	}

	public saveWebcast(webcast: EditWebcastModel): Promise<any> {
		return this.uploadWebcastImages(webcast).then(() => {
			const command = this.buildWebcastCommand(webcast);
			return webcast.id ?
				this.PushService.dispatchCommand('scheduledEvents:SaveWebcastDetails', command, 'WebcastDetailsSaved') :
				this.PushService.dispatchCommand('scheduledEvents:ScheduleWebcast', command, 'WebcastScheduled');
		}).then(result => {
			const webcastId = result.message.webcastId;
			webcast.id = webcastId;
			return Promise.all([
				this.uploadPresentation(webcast),
				this.savePolls(webcast)
			]);
		}).catch(err => {
			console.error(err);
			if (err === UploadFailedError) {
				return Promise.reject(UploadFailedError);
			}

			if (!err.hasIssue) {
				return Promise.reject();
			}

			return Promise.reject(
				FORM_ERRORS.find(error => err.hasIssue(error))
			);
		});
	}

	public saveTemplateFromWebcastId(templateName: string, webcastId: string): Promise<void> {
		return this.getWebcast(webcastId)
			.then(webcast => this.saveWebcastTemplate(templateName, webcast));
	}

	public saveWebcastTemplate(templateName: string, webcast: EditWebcastModel): Promise<any> {
		return this.uploadWebcastImages(webcast)
			.then(() => this.PushService.dispatchCommand('scheduledEvents:AddWebcastTemplate', {
				name: templateName,
				polls: (webcast.polls || []).map(poll => this.PollsService.convertPollForCommand(poll)),
				...this.buildWebcastCommand(webcast, true)
			}));
	}

	public isFormErrorCode(err: string): boolean {
		return FORM_ERRORS.includes(err);
	}

	private buildWebcastCommand(webcast: EditWebcastModel, isTemplateCommand?: boolean): any {
		const features = this.MediaFeatures.accountFeatures;
		const isProducer = webcast.videoSource === WebcastVideoSource.PRODUCER;
		const embeddedContentEnabled = features.webcastEmbedContentSettings?.isAvailable && features.webcastEmbedContentSettings?.isEnabled && webcast.embeddedContent?.isEnabled;
		const bannersEnabled = features.webcastEngagementSettings?.allowSendLinkToAllAttendees && webcast.bannerDetails?.isEnabled;
		const watermarkText = features.viewerIdSettings?.viewerIdPolicy == ViewerIdPolicy.DISABLE ? null :
						   features.viewerIdSettings?.viewerIdPolicy == ViewerIdPolicy.REQUIRE ? features.viewerIdSettings.customInformation : webcast.viewerIdWatermarkText;

		return {
			webcastId: webcast.id,
			cloneWebcastId: webcast.cloneWebcastId,
			title: webcast.title,
			shortcutName: webcast.shortcutName,
			description: webcast.description,
			startDate: webcast.startDate,
			endDate: webcast.endDate,
			timezoneId: webcast.timezoneId,
			isFeatured: webcast.isFeatured,
			videoSource: webcast.videoSource,
			accessControl: webcast.accessControl,
			attendeeJoinMethod: webcast.attendeeJoinMethod,
			chatEnabled: features.webcastEngagementSettings.allowChat && webcast.chatEnabled,
			pollsEnabled: features.webcastEngagementSettings.allowPolls && webcast.pollsEnabled,
			userPollsResponse: webcast.pollsEnabled ? webcast.userPollsResponse : null,
			questionAndAnswerEnabled: webcast.questionAndAnswerEnabled,
			questionOption: webcast.questionOption,
			imageId: webcast.imageId,
			previewThumbnailImageId: webcast.previewThumbnailImageId,
			templateImageId: webcast.templateImageId,
			templatePreviewThumbnailImageId: webcast.templatePreviewThumbnailImageId,
			isBackgroundFill: webcast.isBackgroundFill,
			userIdSlideControl: webcast.moderators.some(({ id }) => webcast.userIdSlideControl === id) ?
				webcast.userIdSlideControl :
				null,
			tags: !isTemplateCommand || features.enableTags ? webcast.tags : [],
			categoryIds: !isTemplateCommand || features.enableCategories ? webcast.categories.map(({ categoryId }) => categoryId) : [],
			unlisted: webcast.unlisted,
			estimatedAttendees: webcast.estimatedAttendees || 0,
			lobbyTimeMinutes: webcast.lobbyTimeMinutes,

			eventAdminIds: webcast.eventAdmins.map(getId),
			moderatorIds: webcast.moderators.map(getId),
			presenterIds: isProducer ? webcast.presenters.map(getId) : null,
			externalPresenters: isProducer && features.enableExternalPresenters ? webcast.externalPresenters.map(p => p.data) : null,
			userIds: webcast.accessControl === EventAccessControl.Private ? filterByType(AccessEntityType.User, webcast.accessEntities) : null,
			groupIds: webcast.accessControl === EventAccessControl.Private ? filterByType(AccessEntityType.Group, webcast.accessEntities) : null,
			password: webcast.accessControl === EventAccessControl.Public ? webcast.password : null,
			registrationFields: webcast.accessControl === EventAccessControl.Public
				? webcast.registrationFields.map(f => ({
					id: f.id,
					includedByHost: !!f.includedByHost
				}))
				: null,
			customFieldValues: (webcast.customFields || []).map(({ id, value, fieldType }) => ({ id, value, fieldType })),

			presentationProfileId: webcast.videoSource === WebcastVideoSource.PRESENTATION ? webcast.presentationProfileId : null,
			webexTeam: webcast.videoSource === WebcastVideoSource.WEBEX ? webcast.webexTeam : null,
			vcSipAddress:
				webcast.videoSource === WebcastVideoSource.SIP_ADDRESS ? webcast.vcSipAddress :
					webcast.videoSource === WebcastVideoSource.PEXIP ? webcast.vcSipAddress :
						webcast.videoSource === WebcastVideoSource.WEBEX ? webcast.webexTeam.sipAddress :
							webcast.videoSource === WebcastVideoSource.ZOOM ? webcast.zoom.meetingPassword
								? `${webcast.zoom.meetingId}.${webcast.zoom.meetingPassword}@${this.MediaFeatures.accountFeatures.zoomSettings.sipAddressSuffix}`
								: `${webcast.zoom.meetingId}@${this.MediaFeatures.accountFeatures.zoomSettings.sipAddressSuffix}`
								: null,
			vcSipPin: webcast.isDtmfSource && webcast.videoSource !== WebcastVideoSource.ZOOM ? webcast.vcSipPin : null,
			vcDtmf: webcast.isDtmfSource ? webcast.vcDtmf : undefined,
			dtmfControlsEnabled: webcast.isDtmfSource ? webcast.vcDtmfControlsEnabled : undefined,
			vcMicrosoftTeamsMeetingUrl: webcast.videoSource === WebcastVideoSource.MSTEAMS ? webcast.vcMicrosoftTeamsMeetingUrl : null,
			automatedWebcast: webcast.videoSource !== WebcastVideoSource.WEBRTC_SINGLE_PRESENTER && webcast.automatedWebcast,
			autoplay: webcast.autoplay,
			closedCaptionsEnabled: webcast.videoSource === WebcastVideoSource.PRESENTATION && webcast.closedCaptionsEnabled,
			disableAutoRecording: webcast.disableAutoRecording,
			autoAssociateVod: webcast.autoAssociateVod,
			recordingUploaderUserId: this.MediaFeatures.accountFeatures.recordingPolicy === RecordingPolicy.DISABLE
				? null
				: (webcast.recordingUploaderUsers?.length > 0 ? webcast.recordingUploaderUsers[0].id : webcast.recordingUploaderUserId),
			preRollVideoId: webcast.preRollVideo?.id,
			postRollVideoId: webcast.postRollVideo?.id,
			redirectVod: webcast.redirectVod,
			presentationFileDownloadAllowed: !!(webcast.presentationFile?.name && webcast.presentationFileDownloadAllowed),
			removePresentationFile: !webcast.presentationFile && !!webcast.originalPresentationFile,

			preProduction: webcast.preProduction && {
				duration: formatTimespan(webcast.preProduction.durationMs),
				groupIds: filterByType(AccessEntityType.Group, webcast.preProduction.attendees),
				userIds: filterByType(AccessEntityType.User, webcast.preProduction.attendees)
			},
			liveSubtitles: webcast.liveSubtitles?.isLiveSubtitlesEnabled ? webcast.liveSubtitles : undefined,
			hideShareUrl: webcast.hideShareUrl,
			enableCustomBranding: webcast.enableCustomBranding,
			brandingSettings: webcast.enableCustomBranding ? {
				headerColor: webcast.brandingSettings.headerColor,
				headerFontColor: webcast.brandingSettings.headerFontColor,
				primaryFontColor: webcast.brandingSettings.primaryFontColor,
				accentColor: webcast.brandingSettings.accentColor,
				accentFontColor: webcast.brandingSettings.accentFontColor,
				primaryColor: webcast.brandingSettings.primaryColor,
				logoImageId: webcast.brandingSettings.logoImage?.id,
				bannerImageId: webcast.brandingSettings.bannerImage?.id,
				speakerProfiles: webcast.brandingSettings.speakerProfiles.map(({ data }) => ({
					name: data.name,
					role: data.role,
					description: data.description,
					avatarImageId: data.avatarImage?.id
				}))
			} : {},
			rtmpSettings: webcast.rtmpSettings ? {
				isSecure: webcast.rtmpSettings.isSecure,
				pushUrl: webcast.rtmpSettings.pushUrl,
				streamKey: webcast.rtmpSettings.streamKey,
				rtmpPort: webcast.rtmpSettings.rtmpPort,
				rtmpsPort: webcast.rtmpSettings.rtmpsPort
			} : null,
			secondarySourceEnabled: webcast.rtmpSettings?.backupStreamEnabled,
			secondaryRtmpSettings: webcast.rtmpSettings?.backupStreamEnabled ? {
				isSecure: webcast.rtmpSettings.isSecure,
				pushUrl: webcast.rtmpSettings.backupStreamUrl,
				streamKey: webcast.rtmpSettings.backupStreamKey,
				rtmpPort: webcast.rtmpSettings.rtmpPort,
				rtmpsPort: webcast.rtmpSettings.rtmpsPort
			} : null,
			emailToPreRegistrants: webcast.emailToPreRegistrants,
			viewerIdEnabled: features?.viewerIdSettings ? VIEWER_ID_SETTING_HELPER[features.viewerIdSettings.viewerIdPolicy](webcast.viewerIdEnabled) : undefined,
			viewerIdWatermarkText: watermarkText,
			vodId: webcast.videoSource === WebcastVideoSource.VOD ? webcast.vodInfo.id : null,
			zoom: webcast.zoom,
			webcastType: webcast.webcastType ?? WebcastType.Rev,
			presenterId: webcast.videoSource === WebcastVideoSource.WEBRTC_SINGLE_PRESENTER ? webcast.presenter?.id : null,
			embeddedContent: {
				isEnabled: embeddedContentEnabled,
				contentLinks: embeddedContentEnabled ? webcast.embeddedContent.contentLinks : []
			},
			bannerDetails: {
				isEnabled: bannersEnabled,
				banners: bannersEnabled ? webcast.bannerDetails.banners : []
			},

			producerBgImages: webcast.videoSource !== WebcastVideoSource.PRODUCER ?
				null :
				webcast.producerBgImages
					.filter(img => !img.clone)
					.map(img => img.id),

			producerBgImagesToClone:  webcast.videoSource !== WebcastVideoSource.PRODUCER ?
				null :
				webcast.producerBgImages
					.filter(img => img.clone)
					.map(img => img.id),

			isCustomConsentEnabled: webcast.isCustomConsentEnabled,
			consentVerbiage: webcast.isCustomConsentEnabled ? webcast.consentVerbiage : null,
			reactionsSettings: {
				enabled: webcast.reactionsSettings.enabled,
				emojis: webcast.reactionsSettings.enabled
					? webcast.reactionsSettings.emojis.map(({ name, char }) => ({ name, character: char }))
					: []
			},
			additionalEmbedDomains: webcast.additionalEmbedDomains
		};
	}

	private uploadPresentation(webcast: EditWebcastModel): Promise<void> | undefined {
		const file = webcast.presentationFile as FileWrapper;
		if (file?.submit) {
			return this.WebcastPresentationService.uploadPresentationFile(file, webcast.id)
				.then(() => {
					webcast.originalPresentationFile = webcast.presentationFile = { name: file.name };
				});
		}
	}

	public uploadImage(file: FileWrapper, webcast: EditWebcastModel): Promise<{ imageId: string, thumbnailUri: string }> {
		if((file as any).fileSubmission) {
			console.error('File already uploaded');
			//return fileSubmission?
		}
		return this.PushService.dispatchCommand(
			webcast.id ? 'scheduledEvents:AddImageToWebcast' : 'media:AddImageToAccount',
			{
				accountId: this.UserContext.getAccount().id,
				webcastId: webcast.id,
				context: 'Webcast'
			},
			'ImageCreated'
		)
			.then(data => {
				const { imageId, uploadUri } = data.message;
				return this.UploadService.uploadFile(file, { id: imageId, uploadUri })
					.then(data => data.message);
			});
	}

	public uploadProducerBgImage(webcast: EditWebcastModel, file: FileWrapper): Promise<void> {
		return this.UploadService.uploadImage('Webcast', file)
			.then(result => {
				const msg = result.message;
				webcast.producerBgImages = [
					...webcast.producerBgImages,
					{
						id: msg.imageId,
						original: msg.thumbnailUri
					}
				];
			});
	}

	private uploadWebcastImages(webcast: EditWebcastModel): Promise<any> {
		const handleImageUpload = (file: FileWrapper, setImg: (id: string) => void): Promise<void> =>
			file
				? this.uploadImage(file, webcast)
					.then(result => setImg(result.imageId))
				: null;

		return Promise.all([
			handleImageUpload(
				webcast.backgroundImageFile,
				id => {
					webcast.imageId = id;
					webcast.backgroundImageFile = null;
				}),
			handleImageUpload(
				webcast.previewThumbnailFile,
				id => {
					webcast.previewThumbnailImageId = id;
					webcast.previewThumbnailFile = null;
				}),
			handleImageUpload(
				webcast.brandingSettings.logoImage?.fileWrapper,
				id => {
					webcast.brandingSettings.logoImage.id = id;
					webcast.brandingSettings.logoImage.fileWrapper = null;
				}),
			handleImageUpload(
				webcast.brandingSettings.bannerImage?.fileWrapper,
				id => {
					webcast.brandingSettings.bannerImage.id = id;
					webcast.brandingSettings.bannerImage.fileWrapper = null;
				}),

			...webcast.brandingSettings.speakerProfiles.map(({ data }) =>
				handleImageUpload(
					data.avatarImage?.fileWrapper,
					id => {
						data.avatarImage.id = id;
						data.avatarImage.fileWrapper = null;
					})),
		]);
	}

	private savePolls(webcast: EditWebcastModel): Promise<void> {
		const polls = webcast.polls || [];
		const pollIds = new Set(polls.map(getId).filter(Boolean));

		const newPolls = polls.filter(poll => webcast.pollsEnabled && !poll.id);

		const updatedPolls = polls.filter(p => {
			const oldPoll = webcast.originalPolls[p.id];

			return webcast.pollsEnabled &&
				p.id &&
				(oldPoll as Poll).totalResponses === 0 &&
				!arePollsEqual(oldPoll, p);
		});

		const deletedPollIds =
			Object.keys(webcast.originalPolls)
				.filter(id => !webcast.pollsEnabled || !pollIds.has(id));

		return Promise.all([
			newPolls.length && this.PollsService.addWebcastPolls(webcast.id, newPolls),
			updatedPolls.length && this.PollsService.updateWebcastPolls(webcast.id, updatedPolls),
			deletedPollIds.length && this.PollsService.deleteWebcastPolls(webcast.id, deletedPollIds)
		])
			.then(([pollIds]) => {
				if (pollIds) {
					pollIds.forEach((id, index) => newPolls[index].id = id);
				}
				webcast.originalPolls = polls.reduce((pollsById, p) => ((pollsById[p.id] = copyPoll(p)), pollsById), {});
			});
	}

	public updateLinkedVideo(webcastId: string, videoId: string, redirectVod: boolean): Promise<void> {
		return this.PushService.dispatchCommand('scheduledEvents:UpdateLinkedVideo', { webcastId, videoId, redirectVod });
	}

	public validateEmbeddedLinkCode(code: string, webcastId?: string): Promise<any> {
		return lastValueFrom(webcastId ?
			this.http.post(`/scheduled-events/${webcastId}/embedded-contents/validation`, { code }) :
			this.http.post(`/scheduled-events/embedded-contents/validation`, { code })
		);
	}
}

function filterByType(type: AccessEntityType, accessEntities: IAccessEntityIdentifier[]): string[] {
	if (!accessEntities || accessEntities.length === 0) {
		return;
	}
	return accessEntities
		.filter(e => e.type === type)
		.map(getId);
}

function getId({ id }: any): string {
	return id;
}

function arePollsEqual(a, b): boolean {
	return isEqual(pollData(a), pollData(b));
}

function pollData(p: any): any {
	return [
		p.question,
		p.answers,
		p.multipleChoice
	];
}

function copyPoll({ question, answers, multipleChoice, totalResponses }: any): any {
	return {
		totalResponses,
		multipleChoice,
		question,
		answers: answers.map(({ text }) => ({ text }))
	};
}
